/**
 * OAuth2 PKCE helpers for Cognito Hosted UI login.
 *
 * Flow:
 *   1. User clicks "Sign In" on /login.
 *   2. We generate a random code_verifier and store it in sessionStorage.
 *   3. We derive a code_challenge from the verifier (SHA-256).
 *   4. We redirect to Cognito's /oauth2/authorize with the challenge.
 *   5. Cognito shows its login UI. User signs in.
 *   6. Cognito redirects to /callback?code=ABC123.
 *   7. /callback reads the code, retrieves the verifier from sessionStorage,
 *      POSTs both to /api/auth/exchange. Backend swaps them for tokens and
 *      sets HttpOnly cookies. Done.
 *
 * Why PKCE: prevents auth-code interception. The /oauth2/token endpoint
 * requires the same code_verifier that produced the code_challenge — so an
 * attacker who somehow intercepts the auth code can't redeem it.
 */

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

/** Where Cognito should redirect back to after login. */
function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

/** Generate a cryptographically random URL-safe string of N bytes (base64url-encoded). */
function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64urlEncode(bytes);
}

/** SHA-256 hash of input string, returned as a Uint8Array. */
async function sha256(input: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(digest);
}

/** Encode bytes as base64url (RFC 4648 § 5) without padding. */
function base64urlEncode(bytes: Uint8Array): string {
  // btoa needs a binary string. String.fromCharCode handles each byte.
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Storage keys. sessionStorage so they're cleared when the tab closes. */
const VERIFIER_KEY = "ashi_pkce_verifier";
const STATE_KEY = "ashi_oauth_state";

/**
 * Begin the OAuth2 PKCE login flow.
 *
 * Generates a code_verifier + state, stores them in sessionStorage, then
 * navigates the browser to Cognito's /oauth2/authorize. The browser leaves
 * the SPA at this point; control returns via the /callback route.
 */
export async function startLogin(): Promise<void> {
  if (!COGNITO_DOMAIN || !CLIENT_ID) {
    throw new Error(
      "Cognito not configured. Check VITE_COGNITO_HOSTED_UI_DOMAIN and VITE_COGNITO_CLIENT_ID.",
    );
  }

  // PKCE code verifier (43-128 random URL-safe chars).
  const codeVerifier = randomString(64);
  sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

  // PKCE code challenge: base64url(sha256(verifier)).
  const challengeBytes = await sha256(codeVerifier);
  const codeChallenge = base64urlEncode(challengeBytes);

  // Random state to defend against CSRF on the callback.
  const state = randomString(16);
  sessionStorage.setItem(STATE_KEY, state);

  // Build the authorize URL and navigate to it.
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(),
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state,
  });

  window.location.assign(`${COGNITO_DOMAIN}/oauth2/authorize?${params}`);
}

/**
 * Validate the state param and retrieve the stored code_verifier.
 *
 * Called by /callback after Cognito redirects back. Throws if state is
 * missing/mismatched (CSRF attempt) or if verifier was lost.
 */
export function consumePkceContext(receivedState: string | null): {
  codeVerifier: string;
  redirectUri: string;
} {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);

  // Clean up regardless of outcome.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!expectedState || !codeVerifier) {
    throw new Error("Login session lost. Please sign in again.");
  }

  if (receivedState !== expectedState) {
    throw new Error("OAuth state mismatch. Please sign in again.");
  }

  return { codeVerifier, redirectUri: redirectUri() };
}